import React, { useState, useEffect, useMemo } from 'react';

import { Card, Title, Flex, Text as TextTremor, TabGroup, TabList, Tab, AreaChart as TremorArea } from './TremorCompat';
import ResizeObserver from 'resize-observer-polyfill';

import { SelectDateRange } from './Common';

import { paramsColors, paramsColorsORP, paramsColorspH } from '../params';

import { COLOURS } from '../styles';
import { orpPhValues } from '../constants';
import { GiBubbles as IconBubbles, GiChemicalDrop as IconChemical } from 'react-icons/gi';

import {
  // MdOutlineEdit as IconEdit,
  // MdOutlineErrorOutline as IconError,
  // MdOutlineInsertChart as IconTrends,
  // MdOutlineSpaceDashboard as IconDashboard,
  // MdQrCode2 as IconQrCode,
  MdOutlineSync as IconRefresh,
  // MdOutlineAddCircleOutline as IconAdd,
  // MdOutlineViewList as IconList,
  // MdOutlineViewCompact as IconGridCompact,
  // MdOutlineViewModule as IconGridExpanded,
  // MdAdd as IconMore,
  // MdRemove as IconLess,
  // MdOutlineArrowDownward as IconAscending,
  // MdOutlineArrowUpward as IconDescending
} from 'react-icons/md';

import {
  Button,
  // ButtonGroup,
  // ButtonToolbar,
  CheckPicker,
  // IconButton,
} from 'rsuite';
import { LoadingView } from './DisplayItems';
import { useDeviceContext } from '../contexts/DeviceContext';
import { useConnectionContext } from '../contexts/ConnectionContext';
import { getApplianceLabel, isApplianceUnconfigured } from '../utils/applianceLabel';
// import {
//   ReferenceLine
// } from "reCharts"

export const TrendsView = () => {
  const {
    configData,
    isLoadingOrNoDevice: getIsLoadingOrNoDevice,
    loading,
    logData,
    phSelection,
    ORPSelection,
    plottableConfigParams,
    plottableParams,
    progress,
    pumpTrackerData,
    selectedDateRange,
    selectedDevice,
    selectedParams,
    refreshData: onRefresh,
    refreshDataByDate: onSelectedDateRangeChanged,
    setSelectedParams: onSelectedParamsChanged,
  } = useDeviceContext();

  // Appliance labels come from the controller's own registers (name + type), which
  // live in ConnectionContext rather than DeviceContext. controllerResponded gates
  // them: registersInit seeds every register with 0, and 0 is "None" in all three
  // type maps, so without the gate an offline device would read as "Not Set".
  const { registerData, controllerResponded } = useConnectionContext();

  // Indexed rather than scanned: registerData holds ~620 entries, and each render walks
  // selectableParams three times (picker labels, disabled values, chart labels), each
  // appliance costing two lookups. A linear find made that O(params x registers) per
  // render; this is one O(registers) pass per registerData change, then O(1) each.
  //
  // First occurrence wins, matching what .find() returned. There are no duplicate
  // addresses today — checked, 619 entries, no collisions — but keeping the semantics
  // identical means a future duplicate cannot change which entry callers see.
  const registerByAddress = useMemo(() => {
    const byAddress = new Map();
    (registerData ?? []).forEach((item) => {
      if (!byAddress.has(item.address)) byAddress.set(item.address, item);
    });
    return byAddress;
  }, [registerData]);
  const getRegisterData = (address) => registerByAddress.get(address);

  // isLoadingOrNoDevice is a function in useDevices' raw return (App.js used
  // to invoke it before passing the boolean down as a prop) — call it fresh
  // each render rather than treating the function reference itself as the
  // (always-truthy) loading flag.
  const isLoadingOrNoDevice = getIsLoadingOrNoDevice();
  const [graphData, setGraphData] = useState([]);
  const [processingData, setProcessingData] = useState(false);
  const [selectableParams, setSelectableParams] = useState([]);
  // Series are keyed by param.text; the chart legend and tooltip should read the same
  // friendly label as the picker. Keyed by text, since that is what selectedParams holds.
  const categoryLabels = Object.fromEntries(
    selectableParams.map((param) => [
      param.text,
      getApplianceLabel(param, getRegisterData, controllerResponded) ?? param.text,
    ]),
  );
  if (!window.ResizeObserver) {
    window.ResizeObserver = ResizeObserver;
  }

  useEffect(() => {
    // console.log("graphData Changed changed- ", logData, configData)
    setProcessingData(true);
    setGraphData(getDataToDisplay(logData, configData));
    setProcessingData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logData, configData]);

  useEffect(() => {
    // console.log("graphData Changed changed- ", logData, configData)
    setProcessingData(true);
    let combinedArrays = [...plottableParams, ...plottableConfigParams];
    setSelectableParams(combinedArrays);
    setProcessingData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plottableParams, plottableConfigParams]);

  function ChooseGraph() {
    const [selectedValue, setSelectedValue] = useState(orpPhValues.orp);

    const pumpCustomToolTip = (props) => {
      const { payload, active } = props;

      const getCategory = (cat) => {
        // console.log( "Data:",cat);
        return cat.tooltip;
      };

      if (!active || !payload) return null;
      return (
        <div className="w-56 rounded-tremor-default border border-tremor-border bg-tremor-background p-2 text-tremor-default shadow-tremor-dropdown mt-3">
          {payload.map((category, idx) => (
            <div key={idx} className="flex flex-1 space-x-2.5">
              <div className={`flex w-1 flex-col bg-${category.payload.color}-500 rounded`} />
              <div className="space-y-1">
                {/* <p className="text-tremor-content">{category.dataKey}</p> */}
                <p className="font-medium text-tremor-content-emphasis">{getCategory(category.payload)}</p>
              </div>
            </div>
          ))}
        </div>
      );
    };

    return isLoadingOrNoDevice ? (
      <div></div>
    ) : (
      <div className="space-y-2 ">
        <Card>
          <Flex className="space-x-2 space-y-2">
            <Title> {selectedValue === orpPhValues.ph ? 'pH Readings' : 'ORP Readings'} </Title>
            <TabGroup onIndexChange={(value) => setSelectedValue(value)} defaultIndex={orpPhValues.orp}>
              <TabList variant="solid" color="zinc">
                <Tab icon={IconBubbles} key="orp" index={orpPhValues.ph} value={orpPhValues.orp}>
                  ORP Readings
                </Tab>
                <Tab icon={IconChemical} key="ph" value={orpPhValues.ph}>
                  pH readings
                </Tab>
              </TabList>
            </TabGroup>
          </Flex>

          {(selectedValue === orpPhValues.ph ? phSelection === false : ORPSelection === false) ? (
            <Flex alignItems="center" className="space-x-2">
              <TextTremor color={COLOURS.warning}>
                {selectedValue === orpPhValues.ph ? 'No pH readings to display' : 'No ORP readings to display'}
              </TextTremor>
            </Flex>
          ) : logData.length === 0 ? (
            <TextTremor color={COLOURS.warning}>No data found</TextTremor>
          ) : (
            <div>
              <TremorArea
                data={graphData}
                index="dateDisplay"
                categories={
                  selectedValue === orpPhValues.ph
                    ? ['pH', 'pH Curr', 'pH Set Point']
                    : ['ORP (mV)', 'ORP Curr (mV)', 'ORP Set Point (mV)']
                }
                colors={selectedValue === orpPhValues.ph ? paramsColorspH : paramsColorsORP}
                // {paramsColors}
                showAnimation={true}
                showLegend={true}
                // startEndOnly={true}
                rotateLabelX={{ angle: -45, verticalShift: 15, xAxisHeight: 50 }}
                className="mt-6 h-80"
                tickGap={100}
                showYAxis={false}
                curveType={'monotone'}
                connectNulls={true}
              ></TremorArea>
              <Title>Pump</Title>

              <TremorArea
                data={pumpTrackerData}
                index="dateDisplay"
                categories={['value']}
                colors={['gray']}
                minValue={-1}
                maxValue={6}
                rotateLabelX={{ angle: -45, verticalShift: 15, xAxisHeight: 50 }}
                tickGap={100}
                showAnimation={true}
                showLegend={true}
                showYAxis={false}
                // startEndOnly={true}
                className="mt-6 h-40"
                curveType={'monotone'}
                customTooltip={pumpCustomToolTip}
                connectNulls={true}
              />
            </div>
          )}
        </Card>
      </div>
    );
  }

  function getDataToDisplay(array1, array2) {
    let combinedArrays = [...array1, ...array2];
    // combinedArrays = [...combinedArrays, ...props.applianceDataArray]
    combinedArrays.sort((a, b) => (a.dateTime < b.dateTime ? -1 : 1));

    return combinedArrays;
  }

  // let time = new Date().getTime();
  // const setActivityTime = (e) => {
  //   time = new Date().getTime();
  // }
  // document.body.addEventListener("mousemove", setActivityTime);
  // document.body.addEventListener("keypress", setActivityTime);

  // const refresh = () => {
  //   if (new Date().getTime() - time >= 10*60000)
  //   {
  //     (onRefresh())
  //  //   window.location.reload(true);
  //   }
  //   else
  //   {
  //     setTimeout(refresh, 10000);
  //   }
  // }

  // setTimeout(refresh, 10000);
  return (
    <div className="mt-3">
      <Flex justifyContent="center">
        <SelectDateRange
          range={selectedDateRange}
          cleanable={false}
          maxDays={30}
          //disabledDate={combine(allowedMaxDays(7), afterToday())}
          onChange={onSelectedDateRangeChanged}
        />
        {/* <Button
          color={COLOURS.default}
          size="sm"
          onClick={onRefresh}
          disabled={(selectedDevice.length === 0) || loading}>Refresh
        </Button> */}

        <Button
          appearance="primary"
          color="blue"
          endIcon={<IconRefresh />}
          size="sm"
          loading={isLoadingOrNoDevice}
          onClick={onRefresh}
        >
          Refresh
        </Button>
      </Flex>

      {isLoadingOrNoDevice || processingData ? (
        <LoadingView progress={progress} />
      ) : selectedDevice.length === 0 ? (
        <Card decoration="bottom" decorationColor={COLOURS.cardDecoration} className="mt-3">
          <TextTremor color={COLOURS.warning}>Select a controller</TextTremor>
        </Card>
      ) : (
        ''
      )}
      <div>{ChooseGraph()}</div>
      {isLoadingOrNoDevice ? (
        <div></div>
      ) : (
        <div>
          <Flex justifyContent="justify-start" spaceX="space-x-2">
            <CheckPicker
              // label is display-only; value stays param.text because that text is
              // also the token held in selectedParams, the chart's category, and the
              // graph data key (item[curr_param.text]). Changing only the label keeps
              // all three intact, so no series can stop resolving.
              data={selectableParams.map((param) => ({
                label: getApplianceLabel(param, getRegisterData, controllerResponded) ?? param.text,
                value: param.text,
              }))}
              // Slots the controller reports as unused (type None, no name) cannot be
              // ticked — plotting one draws a series for hardware wired to nothing.
              // Anything already selected is excluded from this list, or a slot that
              // became unconfigured while selected would be stuck: rsuite blocks
              // toggling a disabled value, deselecting included.
              disabledItemValues={selectableParams
                .filter((param) => isApplianceUnconfigured(param, getRegisterData, controllerResponded))
                .map((param) => param.text)
                .filter((value) => !(selectedParams ?? []).includes(value))}
              value={selectedParams}
              onChange={onSelectedParamsChanged}
              placeholder="Select parameters"
              style={{ width: '100%' }}
              disabled={loading}
            />
          </Flex>
          <Card decoration="bottom" decorationColor={COLOURS.cardDecoration} className="mt-3">
            {
              // (props.pool.length === 0) ? (
              //   <TextTremor color={COLOURS.warning}>No data found</TextTremor>
              // ) :
              <div>
                <TremorArea
                  data={graphData}
                  index="dateDisplay"
                  categories={selectedParams}
                  categoryLabels={categoryLabels}
                  colors={paramsColors}
                  showAnimation={true}
                  showLegend={true}
                  startEndOnly={true}
                  className="mt-6 h-80"
                  connectNulls={true}
                />
              </div>
            }
          </Card>
        </div>
      )}
    </div>
  );
};

{
  /* <AreaChart width="100%" height={400} 
                data={logData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="dateDisplay" />
                <YAxis />
              
                <Tooltip />
                <Area type="monotone" dataKey="Pool" stroke="#8884d8" fillOpacity={1} fill="url(#colorUv)" />
                <Area type="monotone" dataKey="Roof" stroke="#82ca9d" fillOpacity={1} fill="url(#colorPv)" />
                <Area type="monotone" dataKey="Water" stroke="#82ca9d" fillOpacity={1} fill="url(#colorPv)" />
              </AreaChart> */
}
